import { useEffect, useState } from "react";
import axios from "axios";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { DetailDrawer } from "@/components/admin/ui";
import { MILESTONE_TRIGGER_LABELS, formatCents, bpToPercent, percentToBp, dollarsToCents, centsToDollars, blankMilestone } from "./billingMeta";

const labelClass = "text-xs font-bold uppercase tracking-[0.12em] text-slate-400";
const inputClass = "mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-white/15 dark:bg-[#0B1220] dark:text-white";
const btn = "inline-flex items-center gap-1.5 rounded-xl border border-transparent bg-[#0052FF] px-3.5 py-2 text-xs font-bold text-white transition-colors duration-150 hover:bg-[#0040CC] disabled:cursor-not-allowed disabled:opacity-50";
const outlineBtn = "inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-3.5 py-2 text-xs font-bold text-slate-600 transition-colors duration-150 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/15 dark:text-slate-300 dark:hover:bg-white/10";

export const PaymentScheduleFormModal = ({ API, onClose, onSaved }) => {
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [leads, setLeads] = useState([]);
  const [leadId, setLeadId] = useState("");
  const [standaloneTotal, setStandaloneTotal] = useState("");
  const [rows, setRows] = useState([blankMilestone()]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/org/billing/customers`, { withCredentials: true }).then(({ data }) => setCustomers(data.customers || [])).catch(() => {});
  }, [API]);

  useEffect(() => {
    if (!customerId) { setLeads([]); return; }
    axios.get(`${API}/org/billing/customers/${customerId}/leads`, { withCredentials: true }).then(({ data }) => setLeads(data.leads || [])).catch(() => {});
  }, [API, customerId]);

  const selectedLead = leads.find((l) => l.lead_id === leadId);
  const hasAcceptedProposal = selectedLead?.proposal_status === "Accepted";

  const updateRow = (idx, field, value) => setRows((r) => r.map((row, i) => (i === idx ? { ...row, [field]: value } : row)));
  const removeRow = (idx) => setRows((r) => r.filter((_, i) => i !== idx));
  const addRow = () => setRows((r) => [...r, blankMilestone()]);

  const percentTotal = rows.filter((r) => r.amount_type === "percentage").reduce((s, r) => s + (parseFloat(r.percentage_bp) || 0), 0) / 100;

  const submit = async () => {
    if (!customerId) { setError("Choose a Customer"); return; }
    setSaving(true); setError("");
    const payload = {
      customer_id: customerId,
      lead_id: leadId || null,
      standalone_total_cents: !hasAcceptedProposal && standaloneTotal ? dollarsToCents(standaloneTotal) : null,
      milestones: rows.map((r) => ({
        label: r.label, amount_type: r.amount_type,
        percentage_bp: r.amount_type === "percentage" ? percentToBp(r.percentage_bp) : null,
        amount_cents: r.amount_type === "fixed" ? dollarsToCents(centsToDollars(r.amount_cents)) : null,
        trigger_type: r.trigger_type, trigger_date: r.trigger_type === "specific_date" ? r.trigger_date : null,
      })).filter((m) => m.label.trim()),
    };
    try {
      await axios.post(`${API}/org/billing/payment-schedules`, payload, { withCredentials: true });
      onSaved();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <DetailDrawer open onClose={onClose} title="New Payment Schedule" subtitle="Deposit, milestone and final-balance obligations for a Work Job." testId="billing-schedule-form-drawer" wide>
      <div className="space-y-5" data-testid="billing-schedule-form">
        {error && <p className="text-sm font-semibold text-red-600 dark:text-red-400" data-testid="billing-schedule-form-error">{error}</p>}

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className={labelClass}>Customer</label>
            <select value={customerId} onChange={(e) => { setCustomerId(e.target.value); setLeadId(""); }} data-testid="billing-schedule-customer-select" className={inputClass}>
              <option value="">Select Customer…</option>
              {customers.map((c) => <option key={c.customer_id} value={c.customer_id}>{c.first_name} {c.last_name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Work Job / Lead</label>
            <select value={leadId} onChange={(e) => setLeadId(e.target.value)} disabled={!customerId} data-testid="billing-schedule-lead-select" className={inputClass}>
              <option value="">Standalone (no Lead)</option>
              {leads.map((l) => <option key={l.lead_id} value={l.lead_id}>{l.display_id}{l.proposal_status === "Accepted" ? " — Accepted Proposal" : ""}</option>)}
            </select>
          </div>
        </div>

        {leadId && !hasAcceptedProposal && (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700 dark:bg-amber-500/10 dark:text-amber-400" data-testid="billing-schedule-no-accepted-proposal-notice">
            This Lead has no Accepted Proposal — this schedule will be standalone and won't reconcile against a commercial total.
          </p>
        )}
        {(!leadId || !hasAcceptedProposal) && (
          <div>
            <label className={labelClass}>Total Amount ($) — only needed for percentage-based milestones</label>
            <input type="number" step="0.01" value={standaloneTotal} onChange={(e) => setStandaloneTotal(e.target.value)} data-testid="billing-schedule-standalone-total-input" className={inputClass} />
          </div>
        )}

        <div className="space-y-2">
          <p className={labelClass}>Milestones</p>
          {rows.map((row, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 rounded-xl border border-slate-200 p-2.5 dark:border-white/10" data-testid={`billing-schedule-milestone-row-${idx}`}>
              <input value={row.label} onChange={(e) => updateRow(idx, "label", e.target.value)} placeholder="Label (e.g. Deposit)" data-testid={`billing-schedule-milestone-label-${idx}`} className="col-span-3 rounded-lg border border-slate-200 px-2 py-1.5 text-xs dark:border-white/15 dark:bg-[#0B1220] dark:text-white" />
              <select value={row.amount_type} onChange={(e) => updateRow(idx, "amount_type", e.target.value)} data-testid={`billing-schedule-milestone-amount-type-${idx}`} className="col-span-2 rounded-lg border border-slate-200 px-2 py-1.5 text-xs dark:border-white/15 dark:bg-[#0B1220] dark:text-white">
                <option value="percentage">%</option><option value="fixed">$</option>
              </select>
              {row.amount_type === "percentage" ? (
                <input type="number" step="0.01" value={bpToPercent(row.percentage_bp)} onChange={(e) => updateRow(idx, "percentage_bp", percentToBp(e.target.value))} placeholder="%" data-testid={`billing-schedule-milestone-value-${idx}`} className="col-span-2 rounded-lg border border-slate-200 px-2 py-1.5 text-xs dark:border-white/15 dark:bg-[#0B1220] dark:text-white" />
              ) : (
                <input type="number" step="0.01" value={centsToDollars(row.amount_cents)} onChange={(e) => updateRow(idx, "amount_cents", dollarsToCents(e.target.value))} placeholder="$" data-testid={`billing-schedule-milestone-value-${idx}`} className="col-span-2 rounded-lg border border-slate-200 px-2 py-1.5 text-xs dark:border-white/15 dark:bg-[#0B1220] dark:text-white" />
              )}
              <select value={row.trigger_type} onChange={(e) => updateRow(idx, "trigger_type", e.target.value)} data-testid={`billing-schedule-milestone-trigger-${idx}`} className="col-span-3 rounded-lg border border-slate-200 px-2 py-1.5 text-xs dark:border-white/15 dark:bg-[#0B1220] dark:text-white">
                {Object.entries(MILESTONE_TRIGGER_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              {row.trigger_type === "specific_date" && (
                <input type="date" value={row.trigger_date} onChange={(e) => updateRow(idx, "trigger_date", e.target.value)} data-testid={`billing-schedule-milestone-date-${idx}`} className="col-span-1 rounded-lg border border-slate-200 px-1 py-1.5 text-xs dark:border-white/15 dark:bg-[#0B1220] dark:text-white" />
              )}
              <button type="button" onClick={() => removeRow(idx)} data-testid={`billing-schedule-milestone-remove-${idx}`} className="col-span-1 rounded-lg border border-slate-200 text-xs font-bold text-red-500 hover:bg-red-50 dark:border-white/15">✕</button>
            </div>
          ))}
          <button type="button" onClick={addRow} data-testid="billing-schedule-add-milestone-btn" className={outlineBtn}>+ Add Milestone</button>
          {percentTotal > 0 && <p className="text-xs font-bold text-slate-500 dark:text-slate-400" data-testid="billing-schedule-percent-total">Percentage-based milestones total: {percentTotal}%</p>}
        </div>

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} data-testid="billing-schedule-form-cancel" className={outlineBtn}>Cancel</button>
          <button type="button" onClick={submit} disabled={saving} data-testid="billing-schedule-form-save" className={btn}>Create Payment Schedule</button>
        </div>
      </div>
    </DetailDrawer>
  );
};
